import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Container,
  Group,
  Title,
  Text,
  Button,
  ActionIcon,
  Stack,
  Loader,
  Center,
  Alert,
  Modal,
  Tooltip,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  ArrowLeft,
  Eye,
  AlertCircle,
  Layers,
  Zap,
  BookOpen,
  Users,
  Copy,
  Check,
  CheckCircle,
} from 'react-feather';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFDocumentLoadingTask } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';
import {
  sessionContentApi,
  type SessionContentFile,
  type SessionContentHeader,
} from '../../services/api/sessionContent';
import {
  sessionAgendaApi,
  type SessionAgendaSection,
  type SessionAgendaPreSessionWork,
} from '../../services/api/sessionAgenda';
import { sessionNumberConfigsApi } from '../../services/api/sessionNumberConfigs';
import type { Topic } from '../../types';
import { SessionContentUploadSlot } from './components/sessions/SessionContentUploadSlot';
import { SessionDetailsPanel } from './components/sessions/agenda/SessionDetailsPanel';
import { AgendaSectionsPanel } from './components/sessions/agenda/AgendaSectionsPanel';
import { ParticipantPreSessionWorkPanel } from './components/sessions/agenda/ParticipantPreSessionWorkPanel';
import { SessionIncompleteBanner } from './components/sessions/agenda/IncompleteIndicator';
import {
  DuplicateFromSessionModal,
  type DuplicateFromTarget,
} from './components/sessions/agenda/DuplicateFromSessionModal';
import type { SessionAgendaDuplicateFromResponse } from '../../services/api/sessionAgenda';
import { SessionContentPreviewModal } from './components/sessions/agenda/SessionContentPreviewModal';
import { useFeatureFlag } from '../../stores/featureFlags';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';

/**
 * MED-981 (E1.3f) — empty Participant Pre-Session Work state. Used as the
 * initial draft value before the first agenda GET completes so the
 * pre-session work panel renders without flashing undefined values.
 */
const EMPTY_PRE_SESSION_WORK: SessionAgendaPreSessionWork = {
  commitment_review_enabled: false,
  reading_tasks: null,
  reflection_prompts: [],
};

GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * MED-975 / E1.3d — Session Content Editor (Part 1 of 2: Authoring).
 *
 * Replaces MED-784's parsed-Google-Doc model with structured authoring. The
 * CM builds the session's agenda directly:
 *   - Session Details (editable name + multi-line description)
 *   - Slide Deck upload (carried over from MED-784 — Section Visuals pick
 *     from this deck)
 *   - Agenda Sections list (add / edit / reorder / delete; per-section
 *     name, duration, slide visuals, and Leader Prompts)
 *
 * Page-level orchestration:
 *   - Loads agenda + content files in parallel on mount.
 *   - When a slide_deck row is ready, loads its PDF once and passes the
 *     PDFDocumentProxy down to every SectionVisualsPicker (avoids N reloads).
 *   - Holds draft state for description / sections / session name; Save
 *     persists all three; Cancel navigates back; useBlocker prompts on
 *     unsaved-changes navigation (AC-S1, AC-S2, AC-S3).
 *   - Replacing or removing the slide deck clears all visual associations
 *     in local state (AC-SDK2 — the confirmation message warning is
 *     injected into the upload slot's modal).
 *
 * Read-side rendering of the authored content into coach prep / live
 * session views is e1.3h (MED-976) — not in this story.
 */
export function SessionContentEditorPage() {
  // MED-1086 (E1.3m): the editor is reachable two ways — by a concrete
  // sessionId (`/sessions/:sessionId/content`, groups exist) or by session
  // number (`/sessions/by-number/:sessionNumber/content`, no groups yet). Both
  // edit the same (cohort, group_coaching, session_number) scope.
  // MED-1110: "Start from existing" now works in BOTH modes (by-number copies
  // agenda content only — no deck). The slide deck upload and Preview remain
  // hidden in by-number mode: both are genuinely sessionId-bound (the deck's S3
  // key embeds a session id; Preview targets a concrete group session row).
  const { cohortId, sessionId, sessionNumber: sessionNumberParam } = useParams<{
    cohortId: string;
    sessionId: string;
    sessionNumber: string;
  }>();
  const byNumber = sessionNumberParam != null;
  const sessionNumberFromRoute = sessionNumberParam ? Number(sessionNumberParam) : null;
  // MED-1110 — the duplicate-from target follows the addressing mode: a concrete
  // sessionId (groups exist → deck also copied) or a session number (pre-groups →
  // agenda content only). Null only in the degenerate "no addressing" case the
  // early return below already rejects.
  const duplicateTarget: DuplicateFromTarget | null =
    sessionId != null
      ? { mode: 'session', sessionId }
      : sessionNumberFromRoute != null
        ? { mode: 'byNumber', sessionNumber: sessionNumberFromRoute }
        : null;
  const navigate = useNavigate();
  // AC-FF1 / MED-890: Page is gated behind live_session_delivery flag (fail
  // closed while loading per the flag store semantics).
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  // Server state
  const [files, setFiles] = useState<SessionContentFile[] | null>(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [header, setHeader] = useState<SessionContentHeader | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Server-loaded agenda baseline — used for dirty detection.
  // MED-1091 (E1.3n / AC4b): `name`/topic is intentionally NOT part of the
  // baseline — the topic persists immediately on confirm and is excluded from
  // dirty-state. Only description + sections + pre-session work are tracked.
  const [baseline, setBaseline] = useState<{
    description: string;
    sections: SessionAgendaSection[];
    preSessionWork: SessionAgendaPreSessionWork;
    sessionNumber: number | null;
  } | null>(null);

  // MED-1091 (E1.3n / AC5) — the RESOLVED session-number topic shown in the
  // "Session Topic" field. Read from the agenda GET (session_number_config.topic
  // with the legacy fallback), NOT sessions.title. Persisted immediately on
  // confirm (AC4) so it lives outside draft/dirty state (AC4b).
  const [topic, setTopic] = useState<string | null>(null);

  // Local editable draft
  const [draftDescription, setDraftDescription] = useState('');
  const [draftSections, setDraftSections] = useState<SessionAgendaSection[]>([]);
  // MED-981 (E1.3f) — Participant Pre-Session Work panel draft state.
  const [draftPreSessionWork, setDraftPreSessionWork] = useState<SessionAgendaPreSessionWork>(
    EMPTY_PRE_SESSION_WORK,
  );

  // MED-980 (E1.3e / AC-SCF2, AC-SLC2). Cohort-level commitment-tracking
  // flag from the agenda bundle — gates the per-section toggles in the
  // editor. Initialized false so we never accidentally render the toggles
  // before the first load completes.
  const [cohortCommitmentTrackingEnabled, setCohortCommitmentTrackingEnabled] = useState(false);

  // MED-1074 (E1.3j / AC6). The cohort modality's configured session length,
  // from the agenda bundle. The sticky-header running total is compared against
  // it. Null when the modality has no configured length — the header then shows
  // the total alone and suppresses the over-length warning (AC7). Initialized
  // null so we never compare against a stale/assumed value before first load.
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState<number | null>(null);

  // MED-1232 (E1.3 / AC6, AC8) — the persisted one-way "Mark Complete" release
  // state for this scope, from the agenda bundle. Drives whether the header
  // shows the Mark Complete button (in progress) or the completed-state
  // indicator (released). Editing after release keeps this true (AC8).
  const [markedComplete, setMarkedComplete] = useState(false);

  const [saving, setSaving] = useState(false);

  // Slide deck PDF — loaded once per editor session and passed down.
  const [slideDeckDoc, setSlideDeckDoc] = useState<PDFDocumentProxy | null>(null);
  // pdfjs v6 destroys a document through its loading task, so hold the task
  // that produced the current slideDeckDoc for teardown (pentest F-2026-20575).
  const slideDeckTaskRef = useRef<PDFDocumentLoadingTask | null>(null);

  // MED-982 (E1.3g) — "Start from an existing session" flow state. The
  // overwrite-confirm modal sits in front of the picker when the editor's
  // current draft already has content (AC-CB4); the picker handles the
  // two-step cohort → session select and fires the duplicate-from POST on
  // its own.
  const [duplicatePickerOpen, setDuplicatePickerOpen] = useState(false);
  const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);

  // MED-1232 (E1.3 / AC9) — Preview is now an in-editor modal that renders the
  // current draft directly (see SessionContentPreviewModal). This supersedes
  // MED-1231's navigate-to-CoachActiveSessionPreviewPage + "Save before
  // previewing?" dance: the preview reads live draft state, so there is nothing
  // to save first and no unmount that could lose work, and it works in
  // by-number mode (no groups) and regardless of complete/marked state.
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // MED-1232 (E1.3 / AC4) — the Mark Complete confirmation gate + in-flight
  // flag for the one-way release action.
  const [markCompleteConfirmOpen, setMarkCompleteConfirmOpen] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  // `preserveAgendaDraft` (MED-982): when true, reload still refreshes the
  // content-files list (so a server-side change like a copied slide deck shows
  // up) and re-reads the server agenda into `baseline`, but does NOT overwrite
  // the agenda draft setters. The duplicate-from flow needs this: the copied
  // agenda lives only in draft state (AC-S1 — not persisted until Save), so a
  // plain reload would re-fetch the still-empty server agenda and clobber the
  // just-populated draft. Leaving baseline = server (empty) state is what keeps
  // the copied draft correctly `isDirty` so Save stays enabled.
  const reload = useCallback(
    async (opts?: { preserveAgendaDraft?: boolean }) => {
      if (!cohortId) return;
      if (!sessionId && sessionNumberFromRoute == null) return;
      try {
        // MED-1086: dispatch by addressing mode. By-number returns the same
        // shapes (empty files list pre-groups) so everything below is identical.
        const [contentRes, agendaRes] =
          sessionId != null
            ? await Promise.all([
                sessionContentApi.list(cohortId, sessionId),
                sessionAgendaApi.get(cohortId, sessionId),
              ])
            : await Promise.all([
                sessionContentApi.listByNumber(cohortId, sessionNumberFromRoute!),
                sessionAgendaApi.getByNumber(cohortId, sessionNumberFromRoute!),
              ]);
        setFiles(contentRes.files);
        setAiEnabled(contentRes.ai_features_enabled);
        setHeader(contentRes.session);
        // MED-1091 (E1.3n / AC5): the "Session Topic" field reads the RESOLVED
        // session-number topic from the agenda GET (session_number_config.topic,
        // falling back to the legacy topic name) — NOT sessions.title. This is
        // the read/write mismatch fix: the field now reads the same column the
        // Save path writes (AC6 round-trip). Topic is never part of the agenda
        // draft, so it survives a preserveAgendaDraft reload too.
        setTopic(agendaRes.topic ?? null);
        const initialDescription = agendaRes.description ?? '';
        const initialSections = agendaRes.sections;
        const initialPreSessionWork = agendaRes.participant_pre_session_work;
        if (!opts?.preserveAgendaDraft) {
          setDraftDescription(initialDescription);
          setDraftSections(initialSections);
          setDraftPreSessionWork(initialPreSessionWork);
        }
        setCohortCommitmentTrackingEnabled(agendaRes.cohort_commitment_tracking_enabled);
        // MED-1074 (E1.3j / AC6) — configured session length for the over-length
        // comparison. Survives `preserveAgendaDraft` reloads (the length is
        // server-owned, never part of the draft the duplicate-from flow guards).
        setSessionDurationMinutes(agendaRes.session_duration_minutes);
        // MED-1232 (E1.3) — the release flag is server-owned and never part of
        // the agenda draft, so it survives preserveAgendaDraft reloads too.
        setMarkedComplete(agendaRes.marked_complete);
        setBaseline({
          description: initialDescription,
          sections: initialSections,
          preSessionWork: initialPreSessionWork,
          sessionNumber: agendaRes.session_number,
        });
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load session content.');
      }
    },
    [cohortId, sessionId, sessionNumberFromRoute],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  // Slide deck row lookup — used both to gate SectionVisualsPicker and to
  // know when to (re)load the PDF doc.
  const slideDeckFile = useMemo(
    () => files?.find(f => f.slot === 'slide_deck') ?? null,
    [files],
  );
  const slideDeckReady = slideDeckFile?.processing_state === 'ready';
  const hasSlideDeck = !!slideDeckFile && slideDeckFile.processing_state !== 'failed';

  // (Re)load the slide deck PDF when the slide_deck row becomes ready, or
  // when its id changes (replace flow). Tear down on unmount + when the
  // ready row goes away.
  useEffect(() => {
    if (!cohortId || !sessionId) return;
    if (!slideDeckReady || !slideDeckFile) {
      // Free any previously-held doc — the deck was removed or is reprocessing.
      void slideDeckTaskRef.current?.destroy();
      slideDeckTaskRef.current = null;
      setSlideDeckDoc(null);
      return;
    }
    let cancelled = false;
    sessionContentApi
      .fetchPdf(cohortId, sessionId, 'slide_deck')
      .then(async buffer => {
        if (cancelled) return;
        // PDF scripting stays off (canvas-only render, no annotation layer),
        // so no getDocument scripting flag is needed (pentest F-2026-20575).
        const task = getDocument({ data: buffer, useSystemFonts: true });
        const loaded = await task.promise;
        if (cancelled) {
          await task.destroy();
          return;
        }
        // Replace flow: destroy the previous deck's task before swapping.
        void slideDeckTaskRef.current?.destroy();
        slideDeckTaskRef.current = task;
        setSlideDeckDoc(loaded);
      })
      .catch(() => {
        // Best-effort — the SectionVisualsPicker shows nothing without the doc.
      });
    return () => {
      cancelled = true;
    };
    // Depend on slideDeckFile.id so a replace cycles the load.
  }, [cohortId, sessionId, slideDeckReady, slideDeckFile]);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      void slideDeckTaskRef.current?.destroy();
    };
    // intentionally empty deps: only fire on unmount
  }, []);

  // Poll the content slot while the deck is in-flight (carried from MED-784).
  useEffect(() => {
    if (!files) return;
    const inFlight = files.some(
      f => f.processing_state === 'pending' || f.processing_state === 'processing',
    );
    if (!inFlight) return;
    const interval = setInterval(() => {
      void reload();
    }, 4000);
    return () => clearInterval(interval);
  }, [files, reload]);

  // -------- dirty detection ----------------------------------------------
  // Stringify is fine here — the editor's full state is small (sections +
  // prompts cap at low tens). For finer-grained dirty tracking we'd need
  // structural diff; not worth it now.
  // MED-1091 (E1.3n / AC4b): the topic is NOT included here — it saves
  // immediately on confirm, so it never contributes to "unsaved changes."
  // Description, sections, and pre-session work still gate the editor's Save.
  const isDirty = useMemo(() => {
    if (!baseline) return false;
    if (baseline.description !== draftDescription) return true;
    if (JSON.stringify(baseline.sections) !== JSON.stringify(draftSections)) return true;
    // MED-981: include the pre-session work block in dirty detection so Save
    // turns on when the CM toggles Commitment Review, edits Reading/Tasks,
    // or modifies reflection prompts.
    if (
      JSON.stringify(baseline.preSessionWork) !== JSON.stringify(draftPreSessionWork)
    ) {
      return true;
    }
    return false;
  }, [baseline, draftDescription, draftSections, draftPreSessionWork]);

  // MED-982 (E1.3g / AC-CB4) — does the editor's CURRENT draft state hold any
  // content worth warning about before an overwrite? Uses draft state rather
  // than the saved baseline so that even unsaved-but-typed content triggers
  // the overwrite warning (CMs intuitively expect "I typed stuff" to count).
  const editorHasContent = useMemo(() => {
    if (draftSections.length > 0) return true;
    if (draftDescription.trim().length > 0) return true;
    if (draftPreSessionWork.commitment_review_enabled) return true;
    if (draftPreSessionWork.reading_tasks !== null) return true;
    if (draftPreSessionWork.reflection_prompts.length > 0) return true;
    return false;
  }, [draftSections, draftDescription, draftPreSessionWork]);

  // MED-1073 (E1.3i / AC-LP1, AC-LP2) — unsaved-changes leave-prompt. The
  // editor's own exit affordances (Cancel + back-arrow) route through
  // handleAttemptedNavigation below; useUnsavedChangesGuard covers everything
  // else while dirty: browser tab close/refresh (beforeunload), in-app
  // sidebar/link navigation (capture-phase anchor interception — the e1.3d
  // gap this story fixes), and the browser Back/Forward buttons (popstate).
  // All paths funnel into the single "Leave without saving?" modal.
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  useUnsavedChangesGuard({
    when: isDirty,
    onAttempt: proceed => setPendingNavigation(() => proceed),
  });

  const handleAttemptedNavigation = (navigate: () => void) => {
    if (isDirty) {
      setPendingNavigation(() => navigate);
    } else {
      navigate();
    }
  };

  // -------- minimum-complete computation (AC-MC1/MC4) --------------------
  //
  // e1.3d AC-MC1 — "every section has name + duration" (or no sections at all).
  // MED-1182 (E1.3f-R1) — this is now the SOLE completeness input; the former
  // "≥2 reflection prompts" rule was removed (pre-session work is optional).
  const sectionsMissingRequiredFields = useMemo(() => {
    if (draftSections.length === 0) return true;
    return draftSections.some(
      s =>
        !s.section_name ||
        s.section_name.trim().length === 0 ||
        s.duration_minutes === null ||
        s.duration_minutes === undefined,
    );
  }, [draftSections]);

  // MED-1182 (E1.3f-R1) — reflection prompts are optional and no longer feed
  // completeness. Session readiness is governed by the agenda alone.
  const isSessionIncomplete = sectionsMissingRequiredFields;

  // MED-1224 — a section whose duration is positively entered as 0 (or a
  // negative) is invalid and must hard-block Save. This is deliberately
  // narrower than isSessionIncomplete: a blank/null duration is still
  // saveable as in-progress work (AC-MC3), but an explicit 0 is not (the
  // minimum valid duration is 1 minute).
  const hasInvalidDuration = useMemo(
    () =>
      draftSections.some(
        s =>
          s.duration_minutes !== null &&
          s.duration_minutes !== undefined &&
          s.duration_minutes < 1,
      ),
    [draftSections],
  );

  // -------- agenda duration total + over-length warning (MED-1074 / E1.3j) --
  //
  // AC3/AC4/AC5 — running total of section durations, recomputed on every
  // draftSections change (add / edit / remove / reorder). Sections with no
  // duration entered contribute 0 (AC5), so the total reflects only entered
  // durations.
  const agendaDurationTotal = useMemo(
    () => draftSections.reduce((sum, s) => sum + (s.duration_minutes ?? 0), 0),
    [draftSections],
  );

  // AC6 — compare against the cohort modality's configured session length.
  // AC7 — when that length is null (unconfigured modality, reachable in prod),
  // suppress the comparison and the warning rather than assuming a default.
  // AC8/AC10 — over-length is purely advisory; it drives a warning color + a
  // message but never gates Save (AC9 — handleSave is intentionally not coupled
  // to this flag).
  const isOverSessionLength =
    sessionDurationMinutes !== null && agendaDurationTotal > sessionDurationMinutes;

  // -------- save ---------------------------------------------------------
  // Returns true when the save succeeded, false otherwise. MED-1231: the
  // Save & Preview flow awaits this and only navigates to the preview on a
  // confirmed success, so a failed save keeps the CM in the editor with their
  // work intact (rather than navigating away and losing it).
  const handleSave = async (): Promise<boolean> => {
    if (!cohortId || !baseline) return false;
    if (!sessionId && sessionNumberFromRoute == null) return false;
    // MED-1224 (AC1/AC3) — block the save when any section has an invalid
    // duration (0 or negative). The per-section NumberInput already shows the
    // inline "at least 1 minute" error; this guard is the hard stop that keeps
    // an invalid value from ever reaching the API. Blank durations are NOT
    // caught here — they remain saveable as in-progress work (AC-MC3).
    if (hasInvalidDuration) {
      notifications.show({
        title: 'Could not save',
        message: 'Duration must be at least 1 minute for every section.',
        color: 'dangerRed',
      });
      return false;
    }
    setSaving(true);
    try {
      // MED-1091 (E1.3n / AC4b): the topic is NO LONGER persisted here — it
      // saves immediately on confirm via handleTopicSelect. Save handles only
      // description + agenda sections + pre-session work, as before.
      const savePayload = {
        description: draftDescription || null,
        sections: draftSections,
        participant_pre_session_work: draftPreSessionWork,
      };
      const saved =
        sessionId != null
          ? await sessionAgendaApi.save(cohortId, sessionId, savePayload)
          : await sessionAgendaApi.saveByNumber(cohortId, sessionNumberFromRoute!, savePayload);
      // Reset baseline to the post-save canonical state.
      setBaseline({
        description: saved.description ?? '',
        sections: saved.sections,
        preSessionWork: saved.participant_pre_session_work,
        sessionNumber: saved.session_number,
      });
      setDraftSections(saved.sections);
      setDraftDescription(saved.description ?? '');
      setDraftPreSessionWork(saved.participant_pre_session_work);
      // Cohort-level flag may have changed since the GET; sync from the
      // post-save bundle so toggles stay accurate without a hard refresh.
      setCohortCommitmentTrackingEnabled(saved.cohort_commitment_tracking_enabled);
      // MED-1074 (E1.3j / AC6) — keep the comparison length in sync if the
      // cohort's configured session length changed since the GET.
      setSessionDurationMinutes(saved.session_duration_minutes);
      // MED-1232 (E1.3 / AC8) — post-complete edits stay complete; sync the
      // flag from the save response so the completed-state indicator persists.
      setMarkedComplete(saved.marked_complete);
      notifications.show({
        title: 'Session content saved',
        message: saved.marked_complete
          ? 'Saved. Your changes are live for facilitators and participants.'
          : 'Saved as a draft — not yet visible to the cohort. Mark it complete when ready.',
        color: 'medleyGreen',
      });
      return true;
    } catch (err) {
      notifications.show({
        title: 'Could not save',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'dangerRed',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // MED-1091 (E1.3n / AC4, AC8) — topic confirmed in the Select Topic modal.
  // Persists IMMEDIATELY (not via the editor's Save) at session-number scope
  // (session_number_config.topic) using the same write path as the
  // View-by-Session edit — so it applies to all group sessions for this session
  // number and round-trips on the next load (AC6). The field reflects the new
  // value right away with no refresh (AC4); a failed save leaves the prior value
  // in place. Canceling the modal never reaches here, so it makes no change.
  const handleTopicSelect = async (selected: Topic) => {
    if (!cohortId || !baseline || baseline.sessionNumber === null) return;
    const previous = topic;
    // Optimistically reflect the new topic, then confirm against the server.
    setTopic(selected.name);
    try {
      await sessionNumberConfigsApi.setGroupCoachingTopic(
        cohortId,
        baseline.sessionNumber,
        selected.name,
      );
      notifications.show({
        title: 'Topic updated',
        message: `Session topic set to "${selected.name}".`,
        color: 'medleyGreen',
      });
    } catch (err) {
      // Roll back the optimistic update so the field never shows an unsaved value.
      setTopic(previous);
      notifications.show({
        title: 'Could not update topic',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'dangerRed',
      });
    }
  };

  const handleCancel = () => {
    handleAttemptedNavigation(() => navigate(`/cohorts/${cohortId}?tab=sessions`));
  };

  // MED-1232 (E1.3 / AC9) — open the in-editor content preview. It renders the
  // current draft directly (SessionContentPreviewModal), so there is nothing to
  // save first, no unmount that could lose work, and it works before groups
  // exist and regardless of complete/marked state.
  const handlePreviewClick = () => setPreviewModalOpen(true);

  // MED-1232 (E1.3 / AC2–AC5, AC8) — the one-way "Mark Complete" release, run
  // from the confirmation modal (AC4). The server enforces the precondition
  // against the PERSISTED agenda, so we save any unsaved edits first; a failed
  // save aborts before anything is released. On success the flag flips and the
  // header swaps to the completed-state indicator.
  const handleMarkComplete = async () => {
    if (!cohortId) return;
    if (!sessionId && sessionNumberFromRoute == null) return;
    setMarkingComplete(true);
    try {
      if (isDirty) {
        const ok = await handleSave();
        if (!ok) return; // save error already surfaced — nothing released
      }
      const updated =
        sessionId != null
          ? await sessionAgendaApi.markComplete(cohortId, sessionId)
          : await sessionAgendaApi.markCompleteByNumber(cohortId, sessionNumberFromRoute!);
      setMarkedComplete(updated.marked_complete);
      setMarkCompleteConfirmOpen(false);
      notifications.show({
        title: 'Content marked complete',
        message: "This session's content is now visible to facilitators and participants.",
        color: 'medleyGreen',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not mark complete',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'dangerRed',
      });
    } finally {
      setMarkingComplete(false);
    }
  };

  // MED-982 (E1.3g / AC-EP1, AC-CB4) — user clicked "Start from an existing
  // session". If the editor already has content, route through the overwrite
  // warning first (AC-CB4); otherwise open the picker directly.
  const handleStartFromExistingClick = () => {
    if (editorHasContent) {
      setOverwriteConfirmOpen(true);
    } else {
      setDuplicatePickerOpen(true);
    }
  };

  // MED-982 (E1.3g / AC-CB1, AC-CB2, AC-CB6) — picker fired duplicate-from
  // successfully. Pour the response into draft state and reload() so the
  // newly-copied slide deck shows up in the upload slot.
  //
  // The server already reconciled the commitment toggles against the target
  // cohort's commitment_tracking_enabled (AC-CB6), so we don't have to
  // re-gate them here. We DO refresh the local cohort flag from the
  // response in case the cohort's setting changed between the editor's
  // first load and the duplicate-from call (cheap defensive sync).
  const handleDuplicateConfirmed = (payload: SessionAgendaDuplicateFromResponse) => {
    setDraftDescription(payload.description ?? '');
    setDraftSections(payload.sections);
    setDraftPreSessionWork(payload.participant_pre_session_work);
    setCohortCommitmentTrackingEnabled(payload.cohort_commitment_tracking_enabled);
    // Reload pulls fresh content_files (so the copied slide deck shows up in
    // the upload slot panel) and re-reads the agenda into `baseline` (the
    // server agenda is still empty/unchanged — copy isn't persisted until
    // Save). `preserveAgendaDraft` keeps reload from re-fetching that empty
    // server agenda over the draft we just populated above — without it, only
    // the server-persisted slide deck would survive (MED-982 QA bug).
    void reload({ preserveAgendaDraft: true });
    notifications.show({
      title: 'Content loaded',
      message: payload.slide_deck_file
        ? 'Source content loaded into the editor — including the slide deck. Review and save.'
        : 'Source content loaded into the editor. Review and save.',
      color: 'medleyGreen',
    });
  };

  // AC-SDK2: when the slide deck is replaced or removed, clear all visual
  // associations in local draft state. The upload slot's confirmation modal
  // (with `mutationConsequence` text below) gave the CM their chance to
  // back out.
  const handleSlideDeckChange = () => {
    setDraftSections(prev =>
      prev.map(s => ({
        ...s,
        visuals: [], // associations referenced old page numbers — wipe them.
      })),
    );
    // preserveAgendaDraft: the visuals we just cleared live only in draft (not
    // persisted until Save). A plain reload would re-fetch the server agenda,
    // whose sections still carry the OLD visuals, and clobber the clear — so
    // refresh the files list (new/removed deck) without overwriting the draft.
    void reload({ preserveAgendaDraft: true });
  };

  // -------- early returns -----------------------------------------------
  if (!liveSessionDeliveryEnabled) {
    return <Navigate to="/" replace />;
  }

  if (!cohortId || (!sessionId && sessionNumberFromRoute == null)) {
    return (
      <Container size="xl" py="xl">
        <Alert color="dangerRed" icon={<AlertCircle size={16} />}>
          Missing cohort or session in URL.
        </Alert>
      </Container>
    );
  }

  const hasVisualsAcrossSections = draftSections.some(s => s.visuals.length > 0);

  // MED-1232 (E1.3 / AC9) — Preview now works in every mode. It renders the
  // authored draft in a modal (no concrete group session required), so the only
  // reason to disable it is that there is genuinely nothing to preview yet. The
  // former by-number disable is gone (that limitation is exactly what AC9
  // removes).
  const previewDisabledReason = !editorHasContent ? 'Add content to preview.' : null;
  const previewDisabled = previewDisabledReason !== null;

  // MED-1232 (E1.3 / AC3) — Mark Complete precondition: the agenda must be
  // field-complete (>=1 section, each with a name + duration) and free of
  // invalid durations. When unmet, the action is disabled with a clear message
  // about what's missing. The server re-enforces this authoritatively.
  const markCompleteDisabledReason = hasInvalidDuration
    ? "Every section's duration must be at least 1 minute before you can mark this content complete."
    : isSessionIncomplete
      ? 'Add at least one section, and give every section a name and a duration, before marking complete.'
      : null;
  const markCompleteDisabled = markCompleteDisabledReason !== null;

  // MED-1073 (E1.3i / AC-S4) — saved/unsaved status indicator shown beside the
  // top Save. Driven purely by `isDirty`, so it flips the moment the CM edits
  // and clears after a successful Save (which resets baseline → isDirty=false).
  const saveStatusIndicator = isDirty ? (
    <Group gap={6} align="center" c="warningAmber" data-testid="session-content-save-status">
      <Box
        w={8}
        h={8}
        style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-warningAmber-6)' }}
      />
      <Text size="sm" fw={500}>
        Unsaved changes
      </Text>
    </Group>
  ) : (
    <Group gap={6} align="center" c="medleyGreen" data-testid="session-content-save-status">
      <Check size={14} />
      <Text size="sm" fw={500}>
        All changes saved
      </Text>
    </Group>
  );

  // MED-1073 (E1.3i / AC-S3) — the Cancel + Save pair is rendered at both the
  // sticky top and the bottom of the page; both call the identical page-level
  // handlers. `suffix` keeps the top testids byte-for-byte compatible with
  // e1.3d ('') while giving the bottom controls distinct hooks ('-bottom').
  const renderCancelSave = (suffix: '' | '-bottom') => (
    <>
      <Button
        variant="default"
        onClick={handleCancel}
        data-testid={`session-content-cancel-button${suffix}`}
      >
        Cancel
      </Button>
      <Button
        color="medleyGreen"
        onClick={() => void handleSave()}
        loading={saving}
        disabled={!isDirty}
        data-testid={`session-content-save-button${suffix}`}
      >
        Save
      </Button>
    </>
  );

  // MED-1232 (E1.3 / AC2, AC3, AC6) — the Mark Complete control, rendered beside
  // Save. Once released it becomes a static completed-state indicator (AC6) —
  // one-way, so there is NO click-to-undo (unlike MarkAsReviewedControl). Before
  // release it is a distinct action (AC2) with the required tooltip, disabled
  // with a clear message when the precondition isn't met (AC3).
  const renderMarkComplete = (suffix: '' | '-bottom') => {
    if (markedComplete) {
      return (
        <Group
          gap={6}
          align="center"
          wrap="nowrap"
          style={{ padding: '6px 10px' }}
          data-testid={`session-content-complete-indicator${suffix}`}
        >
          <ThemeIcon variant="filled" color="medleyGreen" radius="xl" size="sm">
            <Check size={14} />
          </ThemeIcon>
          <Text size="sm" fw={500} c="medleyGreen">
            Complete
          </Text>
        </Group>
      );
    }
    return (
      <Tooltip
        label={
          markCompleteDisabledReason ??
          'Mark this content ready for facilitators and participants.'
        }
        withArrow
        multiline
        w={260}
      >
        <span style={markCompleteDisabled ? { cursor: 'not-allowed' } : undefined}>
          <Button
            variant="light"
            color="medleyGreen"
            leftSection={<CheckCircle size={16} />}
            onClick={() => setMarkCompleteConfirmOpen(true)}
            disabled={markCompleteDisabled}
            data-testid={`session-content-mark-complete-button${suffix}`}
          >
            Mark Complete
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <Container size="xl" py="xl">
      {/* MED-1073 (E1.3i / AC-S1) — sticky top bar keeps Save + the saved/unsaved
          status visible while the CM scrolls the agenda. Anchored just below the
          AdminLayout AppShell header (its height var) so it never slides under
          the fixed app chrome. */}
      <Box
        style={{
          position: 'sticky',
          top: 'var(--app-shell-header-height, 60px)',
          zIndex: 100,
          backgroundColor: 'var(--medley-bg-main)',
          paddingTop: 'var(--mantine-spacing-md)',
          paddingBottom: 'var(--mantine-spacing-md)',
          marginBottom: 'var(--mantine-spacing-lg)',
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        }}
        data-testid="session-content-sticky-bar"
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group align="center" gap="md">
            <ActionIcon
              variant="filled"
              color="dark"
              size="xl"
              radius="md"
              onClick={() => handleAttemptedNavigation(() => navigate(`/cohorts/${cohortId}?tab=sessions`))}
              aria-label="Back to cohort"
            >
              <ArrowLeft size={20} />
            </ActionIcon>
            <Stack gap={4}>
              <Title order={2}>{header?.title ?? 'Session Content'}</Title>
              {header && (header.cohort_name || header.organization_name) && (
                <Group gap="md" c="dimmed">
                  {header.cohort_name && (
                    <Group gap={4} align="center">
                      <BookOpen size={14} />
                      <Text size="sm">{header.cohort_name}</Text>
                    </Group>
                  )}
                  {header.organization_name && (
                    <Group gap={4} align="center">
                      <Users size={14} />
                      <Text size="sm">{header.organization_name}</Text>
                    </Group>
                  )}
                </Group>
              )}
              {/* AC-L3: subtitle replaces e1.3a's upload-oriented copy. */}
              <Text size="sm" c="dimmed">
                Build the session content for this session.
              </Text>
            </Stack>
          </Group>
          <Group gap="xs" align="center" wrap="nowrap">
            {/* MED-1074 (E1.3j / AC3, AC8, AC10) — running agenda duration total
                in the sticky header. Shown alongside the configured session
                length ("Total: 80 / 60 min"); warning-colored with an icon when
                the total exceeds the length (AC8), neutral otherwise (AC10).
                When no length is configured the comparison is dropped and only
                the total shows (AC7). Updates live with draftSections (AC4). */}
            <Group
              gap={6}
              align="center"
              wrap="nowrap"
              c={isOverSessionLength ? 'warningAmber' : 'dimmed'}
              data-testid="agenda-duration-total"
            >
              {isOverSessionLength && <AlertCircle size={14} />}
              <Text size="sm" fw={500}>
                Total:{' '}
                {sessionDurationMinutes !== null
                  ? `${agendaDurationTotal} / ${sessionDurationMinutes} min`
                  : `${agendaDurationTotal} min`}
              </Text>
            </Group>
            {/* AC-S4 — saved/unsaved status sits immediately left of the top Save. */}
            {saveStatusIndicator}
            {/* MED-982 (E1.3g / AC-EP1) — always-available action; the empty-state
                banner below offers a more prominent CTA when the editor is empty.
                MED-1110: shown in BOTH addressing modes — by-number copies agenda
                content only (no deck). */}
            <Button
              variant="default"
              leftSection={<Copy size={16} />}
              onClick={handleStartFromExistingClick}
              data-testid="session-content-start-from-existing-button"
            >
              Start from existing
            </Button>
            {/* MED-1231 (AC7, AC8): Preview is ALWAYS shown now. When it can't be
                used (no groups yet, or no content), it renders disabled with a
                tooltip explaining why — rather than disappearing. The disabled
                <Button> is wrapped in a <span> so the Tooltip still receives
                hover events (a native disabled button emits none) — matching the
                ButtonToggle disabled-tooltip convention. Clicking (when enabled)
                routes through handlePreviewClick, which saves-then-previews on a
                dirty draft (AC1) or previews directly when clean (AC2). */}
            <Tooltip
              label={previewDisabledReason}
              disabled={!previewDisabled}
              withArrow
              multiline
              w={260}
            >
              <span style={previewDisabled ? { cursor: 'not-allowed' } : undefined}>
                <Button
                  variant="default"
                  leftSection={<Eye size={16} />}
                  onClick={handlePreviewClick}
                  disabled={previewDisabled}
                  data-testid="session-content-preview-button"
                >
                  Preview
                </Button>
              </span>
            </Tooltip>
            {renderMarkComplete('')}
            {renderCancelSave('')}
          </Group>
        </Group>
      </Box>

      {!files && !error && (
        <Center mih={240}>
          <Loader />
        </Center>
      )}

      {error && (
        <Alert color="dangerRed" icon={<AlertCircle size={16} />} title="Could not load content">
          {error}
        </Alert>
      )}

      {files && (
        <Stack gap="md">
          {/* MED-1074 (E1.3j / AC8, AC9) — advisory over-length warning. Names
              the overage and the configured session length. Advisory only:
              Save is never blocked (consistent with e1.3d's no-hard-block
              model — handleSave is not coupled to isOverSessionLength). Shown
              only when over (AC10 hides it at/under length); never shown when
              no length is configured (AC7, since isOverSessionLength is false). */}
          {isOverSessionLength && (
            <Alert
              color="warningAmber"
              variant="light"
              icon={<AlertCircle size={16} />}
              title="Agenda is longer than the session length"
              data-testid="agenda-over-length-warning"
            >
              This agenda totals {agendaDurationTotal} min, which is longer than the{' '}
              {sessionDurationMinutes} min session length. Consider adjusting section
              durations.
            </Alert>
          )}

          {aiEnabled && (
            <Alert
              icon={<Zap size={16} />}
              color="surveyPurple"
              variant="light"
              title="AI features active"
            >
              AI-powered facilitation features are enabled for this session&apos;s modality.
              Your authored content may be used by coaches as input to live nudges and
              post-session distillation.
            </Alert>
          )}

          <SessionIncompleteBanner
            reasons={{
              sectionsMissingRequiredFields,
            }}
          />

          {/* MED-982 (E1.3g / AC-EP1) — prominent CTA shown when the editor has
              no content yet. The header "Start from existing" button stays
              visible regardless; this is the empty-state companion that puts
              the action front-and-center. MED-1110: shown in by-number mode too. */}
          {!editorHasContent && (
            <Alert
              variant="light"
              color="medleyGreen"
              icon={<Copy size={16} />}
              title="Start from an existing session"
            >
              <Stack gap="sm" align="flex-start">
                <Text size="sm">
                  Save time by copying the content from a session in another cohort. You
                  can customize everything afterward — the session name and number stay
                  yours.
                </Text>
                <Button
                  variant="filled"
                  color="medleyGreen"
                  leftSection={<Copy size={14} />}
                  onClick={handleStartFromExistingClick}
                  data-testid="session-content-empty-state-start-from-existing"
                >
                  Browse sessions to copy from
                </Button>
              </Stack>
            </Alert>
          )}

          <SessionDetailsPanel
            topic={topic}
            description={draftDescription}
            sessionNumber={baseline?.sessionNumber ?? null}
            // MED-1091 (E1.3n / AC10): by-number addressing means no groups
            // exist yet (e1.3m pre-groups authoring) — drives the helper-copy
            // variant. sessionId addressing always implies groups exist.
            groupsExist={!byNumber}
            onTopicSelect={selected => void handleTopicSelect(selected)}
            onDescriptionChange={value => setDraftDescription(value)}
          />

          {/* Slide deck slot — retained from MED-784. AC-SDK2: confirmation
              modal carries the slide-association-loss warning. MED-1086: hidden
              in by-number mode — deck upload is sessionId-bound (and the deck is
              optional/deferrable pre-groups per the e1.3m scope). */}
          {!byNumber && sessionId && (
          <SessionContentUploadSlot
            cohortId={cohortId}
            sessionId={sessionId}
            slot="slide_deck"
            file={slideDeckFile}
            required={false}
            label="Slide Deck"
            helperText="Optional. Upload your slide deck as a PDF or PowerPoint. The slides you add to a section come from this deck."
            acceptHint="Max 50 MB · PDF, PPTX, or PPT"
            icon={<Layers size={18} />}
            onChange={handleSlideDeckChange}
            mutationConsequence={
              hasVisualsAcrossSections ? (
                <Alert color="warningAmber" variant="light" icon={<AlertCircle size={14} />}>
                  All section→slide associations will be cleared. You&apos;ll need to re-add
                  slides to each section from the new deck.
                </Alert>
              ) : null
            }
          />
          )}

          <AgendaSectionsPanel
            sections={draftSections}
            hasSlideDeck={hasSlideDeck}
            slideDeckDoc={slideDeckDoc}
            cohortCommitmentTrackingEnabled={cohortCommitmentTrackingEnabled}
            onChange={setDraftSections}
          />

          {/* MED-981 (E1.3f / AC-PANEL1): Participant Pre-Session Work panel
              sits BELOW Agenda Sections per the spec. */}
          <ParticipantPreSessionWorkPanel
            value={draftPreSessionWork}
            onChange={setDraftPreSessionWork}
            cohortCommitmentTrackingEnabled={cohortCommitmentTrackingEnabled}
          />

          {/* MED-1073 (E1.3i / AC-S2, AC-S3) — second Save at the bottom of the
              page so the CM doesn't have to scroll back up after reaching the
              end of a long agenda. Same page-level commit as the sticky top
              Save. */}
          <Group justify="flex-end" gap="xs" mt="md" align="center">
            {renderMarkComplete('-bottom')}
            {renderCancelSave('-bottom')}
          </Group>
        </Stack>
      )}

      {/* MED-982 (E1.3g / AC-CB4) — overwrite confirmation. Only opened when
          the editor's draft already has content; otherwise the header button
          and empty-state CTA open the picker directly. Confirming dismisses
          this modal AND opens the picker; cancel leaves draft state
          untouched. */}
      <Modal
        opened={overwriteConfirmOpen}
        onClose={() => setOverwriteConfirmOpen(false)}
        title="Replace this session's content?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            You have content in the editor. Starting from an existing session will
            <Text span fw={500}> replace </Text>
            the description, agenda sections, slide deck, and pre-session work with the
            source you pick. The session name and number stay the same.
          </Text>
          <Text size="sm" c="dimmed">
            You&apos;ll still get a final confirmation step inside the picker before the
            copy is applied.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOverwriteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                setOverwriteConfirmOpen(false);
                setDuplicatePickerOpen(true);
              }}
              data-testid="session-content-overwrite-confirm-button"
            >
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* MED-982 (E1.3g) — Two-step picker. Self-contained: handles its own
          step transitions, the duplicate-from POST, and the inner confirm
          step. Calls back into onConfirmed with the reconciled payload.
          MED-1110: rendered in both addressing modes; `target` carries the
          session-vs-by-number distinction so the picker hits the right endpoint. */}
      {duplicateTarget && (
        <DuplicateFromSessionModal
          opened={duplicatePickerOpen}
          onClose={() => setDuplicatePickerOpen(false)}
          targetCohortId={cohortId}
          target={duplicateTarget}
          onConfirmed={handleDuplicateConfirmed}
        />
      )}

      {/* AC-S3: unsaved-changes prompt — manual interception of the
          editor's exit affordances (Cancel + back arrow). Browser-level
          navigation (reload, tab close, external link) is caught by the
          beforeunload handler above. */}
      <Modal
        opened={pendingNavigation !== null}
        onClose={() => setPendingNavigation(null)}
        title="Leave without saving?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            You have unsaved changes to this session&apos;s content. Leaving now will
            discard them.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPendingNavigation(null)}>
              Stay
            </Button>
            <Button
              color="dangerRed"
              onClick={() => {
                const go = pendingNavigation;
                setPendingNavigation(null);
                go?.();
              }}
            >
              Leave
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* MED-1232 (E1.3 / AC4) — Mark Complete confirmation. Mark Complete is a
          one-way, user-visible release, so it always confirms before releasing.
          Content is released only on confirm; Cancel makes no change. When the
          draft has unsaved edits, the primary action saves first (the server
          validates the persisted agenda) and then releases. */}
      <Modal
        opened={markCompleteConfirmOpen}
        onClose={() => setMarkCompleteConfirmOpen(false)}
        title="Mark this content complete?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will make the content visible to facilitators and participants. You can keep
            editing afterward and it stays complete — but there is no way to un-mark it.
          </Text>
          {isDirty && (
            <Text size="sm" c="dimmed">
              Your unsaved changes will be saved first.
            </Text>
          )}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setMarkCompleteConfirmOpen(false)}
              data-testid="session-content-mark-complete-cancel-button"
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => void handleMarkComplete()}
              loading={markingComplete}
              data-testid="session-content-mark-complete-confirm-button"
            >
              Mark Complete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* MED-1232 (E1.3 / AC9) — in-editor content preview. Renders the current
          draft directly; works in every mode and regardless of complete state. */}
      <SessionContentPreviewModal
        opened={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        topic={topic}
        description={draftDescription}
        sections={draftSections}
        preSessionWork={draftPreSessionWork}
        sessionDurationMinutes={sessionDurationMinutes}
        commitmentTrackingEnabled={cohortCommitmentTrackingEnabled}
      />
    </Container>
  );
}
